import { useEffect, useState } from "react";
import { api, ZEFFY } from "../../lib/api";
import { toast } from "sonner";
import { Check, ExternalLink, ArrowRight, FileText } from "lucide-react";

export default function Sponsorship() {
  const [tiers, setTiers] = useState([]);
  const [active, setActive] = useState(0);
  const [form, setForm] = useState({
    full_name: "", organization: "", email: "", phone: "",
    tier: "Gold", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/sponsorship/tiers").then((r) => {
      setTiers(r.data);
      setActive(3); // Gold default
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/sponsorship/inquiry", { ...form, tier: tiers[active]?.name || form.tier });
      toast.success("Sponsorship inquiry received. We'll be in touch shortly.");
      setForm({ ...form, full_name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const current = tiers[active];

  return (
    <section
      id="sponsorship"
      data-testid="sponsorship-section"
      className="relative py-24 md:py-32 bg-aawta-charcoal scroll-mt-nav"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-7">
            <p className="text-overline text-aawta-pink mb-4">SPONSORSHIP CENTER</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.05]">
              Anchor a movement.<br />
              <span className="text-gradient-pink">Power a generation.</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-white/60 leading-relaxed">
            Six tiers. One mission. Your brand alongside the leaders shaping the next decade
            of transportation, logistics, and supply chain in America.
          </p>
        </div>

        {/* Tier selector */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-8">
          {tiers.map((t, i) => (
            <button
              key={t.name}
              data-testid={`tier-tab-${i}`}
              onClick={() => setActive(i)}
              className={`text-left p-4 border transition-all ${
                active === i
                  ? "border-aawta-pink bg-aawta-pink/5"
                  : "border-white/10 hover:border-white/30"
              }`}
            >
              <p className={`text-overline ${active === i ? "text-aawta-pink" : "text-white/40"}`}>{t.name}</p>
              <p className="font-display text-2xl text-white mt-2">${(t.price / 1000).toLocaleString()}K</p>
            </button>
          ))}
        </div>

        {/* Active tier card */}
        {current && (
          <div data-testid="active-tier-detail" className="grid md:grid-cols-12 gap-8 border border-white/10 bg-aawta-jet p-8 md:p-12">
            <div className="md:col-span-5">
              <p className="text-overline text-aawta-pink">SELECTED TIER</p>
              <h3 className="font-display text-5xl md:text-6xl text-white font-light mt-3">{current.name}</h3>
              <p className="font-mono text-3xl text-white/80 mt-2">${current.price.toLocaleString()}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={ZEFFY.galaTier}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sponsor-zeffy-btn"
                  className="btn-pink"
                >
                  SECURE TIER <ExternalLink size={14} />
                </a>
                <a href="#sponsor-inquiry" className="btn-ghost">
                  TALK TO US <ArrowRight size={14} />
                </a>
              </div>
              <a
                href="https://gamma.app/docs/The-3rd-Annual-Power-to-Drive-Change-Fundraising-Gala-1d4oj679gv599cz"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sponsor-deck-btn"
                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/50 hover:text-aawta-pink mt-6 transition-colors"
              >
                <FileText size={12} /> DOWNLOAD SPONSORSHIP DECK <ExternalLink size={11} />
              </a>
            </div>
            <div className="md:col-span-7">
              <p className="text-overline text-white/40 mb-5">INCLUDED BENEFITS</p>
              <ul className="space-y-3">
                {current.benefits.map((b) => (
                  <li key={b} className="flex gap-3 text-white/85 text-sm md:text-base">
                    <Check className="text-aawta-pink shrink-0 mt-0.5" size={18} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Inquiry form */}
        <form
          id="sponsor-inquiry"
          onSubmit={submit}
          data-testid="sponsor-form"
          className="mt-14 glass-panel p-8 md:p-10 grid md:grid-cols-12 gap-6 scroll-mt-nav"
        >
          <div className="md:col-span-12">
            <p className="text-overline text-aawta-pink">SPONSORSHIP INQUIRY</p>
            <h4 className="font-display text-2xl md:text-3xl text-white mt-2">Let&rsquo;s build a custom package.</h4>
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Name *</label>
            <input data-testid="spn-name" required className="input-luxe" value={form.full_name} onChange={handle("full_name")} />
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Organization *</label>
            <input data-testid="spn-org" required className="input-luxe" value={form.organization} onChange={handle("organization")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Email *</label>
            <input data-testid="spn-email" required type="email" className="input-luxe" value={form.email} onChange={handle("email")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Phone</label>
            <input data-testid="spn-phone" className="input-luxe" value={form.phone} onChange={handle("phone")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Tier Interest</label>
            <input data-testid="spn-tier" disabled className="input-luxe text-aawta-pink" value={current?.name || form.tier} />
          </div>
          <div className="md:col-span-12">
            <label className="label-luxe">Goals / Message</label>
            <textarea data-testid="spn-message" rows={3} className="input-luxe resize-none" value={form.message} onChange={handle("message")} />
          </div>
          <div className="md:col-span-12">
            <button type="submit" data-testid="spn-submit" disabled={submitting} className="btn-pink disabled:opacity-50">
              {submitting ? "SENDING..." : "REQUEST SPONSORSHIP DETAILS"} <ArrowRight size={14} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
